import {
  getCapabilityDisplayName,
  getProviderCapabilitiesList,
} from "./vendorProfileCapabilities";
import { getRfiCapabilityBadgeDisplayName } from "./rfiCatalogServiceNames";

/** Label for a location category the catalog retired and the vendor's capabilities do not name. */
export const RETIRED_LOCATION_CATEGORY_LABEL = "Retired service category";

const sameId = (a, b) =>
  a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

const capabilityId = (cap) => cap?.rfi_id ?? cap?.rfiId ?? cap?.value;

/**
 * Form label for a capability: the catalog's current name for its id, else its stored
 * name shown under the category that replaced it. A stored copy keeps the name it had
 * when written ("Foundation - All"), and the same row is "Foundation - GCP" today.
 */
function capabilityFormLabel(cap, rfis = []) {
  const id = capabilityId(cap);
  const row = (rfis || []).find((rfi) => sameId(rfi?.rfi_id, id));
  if (row?.rfi_name) return row.rfi_name;
  const stored = getCapabilityDisplayName(cap, rfis);
  return stored ? getRfiCapabilityBadgeDisplayName(stored) : "";
}

/** Map provider API capabilities into the Company Profile multi-select value. */
export function formatProviderCapabilitiesForForm(
  providerData,
  mergeSelected = (selected) => selected,
  rfis = []
) {
  return mergeSelected(
    getProviderCapabilitiesList(providerData).map((cap) => ({
      value: capabilityId(cap),
      label: capabilityFormLabel(cap, rfis),
    }))
  );
}

/** Keep Library Documents services when saving Company Profile capabilities. */
export function formatProviderCapabilitiesForPayload(
  selected,
  mergeSelected = (selectedValue) => selectedValue
) {
  return mergeSelected(selected)
    ?.filter((cap) => cap?.value != null && cap.value !== "")
    .map((cap) => ({
      rfi_id: cap.value,
      rfi_name: cap.label,
    }));
}

/** Ids for a location's `rfi_template_ids`; the API binds them as GUIDs, so a service without an id is left out. */
export function toLocationRfiTemplateIds(selected) {
  return selected
    ?.map((cap) => cap?.value)
    .filter((value) => value != null && value !== "");
}

/**
 * Map a location's stored category ids into its multi-select value. Every id is kept: one
 * the catalog list no longer carries (a retired category) is labelled with the name the
 * vendor's capabilities give that id, shown as the category that replaced it, so saving
 * the form never drops it.
 */
export function formatLocationCategoriesForForm(ids = [], rfis = [], providerData = null) {
  const capabilities = getProviderCapabilitiesList(providerData);
  return (ids || []).filter(Boolean).map((id) => {
    const row = (rfis || []).find((rfi) => sameId(rfi?.rfi_id, id));
    if (row) return { value: row.rfi_id, label: row.rfi_name };
    const capability = capabilities.find((cap) => sameId(capabilityId(cap), id));
    return {
      value: id,
      label: capabilityFormLabel(capability, rfis) || RETIRED_LOCATION_CATEGORY_LABEL,
    };
  });
}
